/**
 * Promo Banner Countdown - Standalone JavaScript
 * Self-contained countdown timer functionality
 * Can be used independently on any website
 *
 * Usage:
 * <div class="promo-banner__countdown"
 *      data-countdown
 *      data-countdown-end="2025-12-31T23:59"
 *      data-countdown-zone="Australia/Melbourne">
 *   <!-- countdown HTML structure -->
 * </div>
 *
 * Then call: PromoCountdown.init('promo-banner-countdown-unique-id');
 */

window.PromoCountdown = window.PromoCountdown || (function () {
  'use strict';

  var timers = window.PromoCountdownTimers || new WeakMap();
  window.PromoCountdownTimers = timers;

  function stop(countdownEl, removeMarker) {
    var timer = timers.get(countdownEl);
    if (timer) {
      clearInterval(timer.intervalId);
      Object.keys(timer.tickTimeouts).forEach(function (key) { clearTimeout(timer.tickTimeouts[key]); });
      Object.keys(timer.valueUpdateTimeouts).forEach(function (key) { clearTimeout(timer.valueUpdateTimeouts[key]); });
      timers.delete(countdownEl);
    }
    if (removeMarker) countdownEl.removeAttribute('data-countdown-inited');
  }

  /**
   * Initialize countdown for a specific section
   * @param {string} sectionId - The ID of the section element
   */
  function init(sectionId) {
    var sectionEl = document.getElementById(sectionId);
    if (!sectionEl) return;

    var countdownEl = sectionEl.querySelector('[data-countdown]');
    if (!countdownEl) return;

    // Guard against double-init (initAll + inline init, or the script loaded
    // by more than one section) — otherwise we stack duplicate setIntervals.
    if (countdownEl.getAttribute('data-countdown-inited') === '1') return;
    countdownEl.setAttribute('data-countdown-inited', '1');

    var endDate;

    // Try to use pre-computed timestamp first (from Liquid)
    var tsRaw = countdownEl.getAttribute('data-countdown-ts');
    if (tsRaw && tsRaw.trim() !== '') {
      var tsNumber = parseInt(tsRaw, 10);
      if (!isNaN(tsNumber)) {
        endDate = new Date(tsNumber * 1000);
      }
    }

    // Fall back to client-side parsing
    if (!endDate) {
      var rawValue = countdownEl.getAttribute('data-countdown-end');
      if (!rawValue || rawValue.trim() === '') {
        countdownEl.setAttribute('data-countdown-state', 'missing-end');
        countdownEl.removeAttribute('data-countdown-inited');
        return;
      }

      var zone = countdownEl.getAttribute('data-countdown-zone') || 'Australia/Melbourne';
      endDate = parseDateInZone(rawValue, zone);
    }

    if (!(endDate instanceof Date) || isNaN(endDate.getTime())) {
      countdownEl.setAttribute('data-countdown-state', 'invalid-end');
      countdownEl.removeAttribute('data-countdown-inited');
      return;
    }

    // Get counter elements
    var counters = {
      days: countdownEl.querySelector('[data-countdown-days]'),
      hours: countdownEl.querySelector('[data-countdown-hours]'),
      minutes: countdownEl.querySelector('[data-countdown-minutes]'),
      seconds: countdownEl.querySelector('[data-countdown-seconds]')
    };

    if (!counters.days || !counters.hours || !counters.minutes || !counters.seconds) {
      countdownEl.removeAttribute('data-countdown-inited');
      return;
    }

    // Track previous values for animation
    var previousValues = {
      days: null,
      hours: null,
      minutes: null,
      seconds: null
    };

    var tickTimeouts = {};
    var valueUpdateTimeouts = {};
    var TICK_DURATION = 650;
    var VALUE_SWAP_DELAY = 360;

    /**
     * Trigger tick animation for a counter
     * @param {string} key - Counter key (days, hours, minutes, seconds)
     * @param {string} nextValue - New value to display
     */
    function triggerTick(key, nextValue) {
      var el = counters[key];
      if (!el) return;

      var item = el.closest ? el.closest('[data-countdown-item]') : null;
      if (!item) {
        item = el.parentNode;
      }
      if (!item) return;

      // Clear existing timeouts
      if (valueUpdateTimeouts[key]) {
        clearTimeout(valueUpdateTimeouts[key]);
      }
      if (tickTimeouts[key]) {
        clearTimeout(tickTimeouts[key]);
      }

      // Restart animation
      item.classList.remove('is-ticking');
      void item.offsetWidth; // Force reflow
      item.classList.add('is-ticking');

      // Update value during animation
      valueUpdateTimeouts[key] = setTimeout(function () {
        el.textContent = nextValue;
        valueUpdateTimeouts[key] = null;
      }, VALUE_SWAP_DELAY);

      // Remove animation class after completion
      tickTimeouts[key] = setTimeout(function () {
        item.classList.remove('is-ticking');
        tickTimeouts[key] = null;
      }, TICK_DURATION);
    }

    /**
     * Update countdown display
     */
    function updateCountdown() {
      if (!countdownEl.isConnected) {
        stop(countdownEl, true);
        return;
      }
      var now = new Date();
      var diff = endDate.getTime() - now.getTime();

      // Check if countdown expired
      if (diff <= 0) {
        countdownEl.setAttribute('data-countdown-state', 'expired');
        sectionEl.style.display = 'none';
        stop(countdownEl, false);
        return;
      }

      // Calculate time units
      var totalSeconds = Math.floor(diff / 1000);
      var days = Math.floor(totalSeconds / 86400);
      var hours = Math.floor((totalSeconds % 86400) / 3600);
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;

      // Keep the live timer visible for the full promotion, matching Liquid.
      var mode = 'clock';
      if (countdownEl.getAttribute('data-countdown-mode') !== mode) {
        countdownEl.setAttribute('data-countdown-mode', mode);
      }

      var displayValues = {
        days: String(days),
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0')
      };

      // Update each counter with animation
      for (var key in displayValues) {
        if (!Object.prototype.hasOwnProperty.call(displayValues, key)) continue;
        var value = displayValues[key];

        if (previousValues[key] !== value) {
          var firstRender = previousValues[key] === null;
          previousValues[key] = value;

          if (firstRender) {
            counters[key].textContent = value;
          } else {
            triggerTick(key, value);
          }
        }
      }
    }

    // Start countdown
    var intervalId = setInterval(updateCountdown, 1000);
    timers.set(countdownEl, {
      intervalId: intervalId,
      tickTimeouts: tickTimeouts,
      valueUpdateTimeouts: valueUpdateTimeouts
    });
    updateCountdown(); // Initial update
  }

  /**
   * Parse date string in specific timezone
   * @param {string} value - Date string (YYYY-MM-DDTHH:MM or YYYY-MM-DDTHH:MM:SS)
   * @param {string} zone - Timezone (e.g., 'Australia/Melbourne')
   * @returns {Date|null} Parsed date or null if invalid
   */
  function parseDateInZone(value, zone) {
    if (!value || typeof value !== 'string') return null;

    // Normalize input
    var normalized = value.trim();
    if (normalized.indexOf('T') === -1 && normalized.indexOf(' ') > -1) {
      normalized = normalized.replace(' ', 'T');
    }
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalized)) {
      normalized += ':00';
    }

    // Handle dates with timezone info
    if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized)) {
      var direct = new Date(normalized);
      if (!isNaN(direct.getTime())) {
        return direct;
      }
    }

    // Parse local timezone date
    var match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
    if (!match) return null;

    var formatter = buildFormatter(zone);
    if (!formatter) return null;

    var year = Number(match[1]);
    var month = Number(match[2]);
    var day = Number(match[3]);
    var hour = Number(match[4]);
    var minute = Number(match[5]);
    var second = Number(match[6]);

    // Convert to UTC
    var desiredUTC = Date.UTC(year, month - 1, day, hour, minute, second);
    var initial = new Date(desiredUTC);

    // Get timezone offset
    var mapped = partsToMap(initial, formatter);
    if (!mapped.year) {
      return new Date(desiredUTC);
    }

    var zonedUTC = Date.UTC(
      Number(mapped.year),
      Number(mapped.month) - 1,
      Number(mapped.day),
      Number(mapped.hour),
      Number(mapped.minute),
      Number(mapped.second)
    );

    // Adjust for timezone
    var diff = desiredUTC - zonedUTC;
    var adjusted = new Date(initial.getTime() + diff);

    // Verify adjustment
    var finalMap = partsToMap(adjusted, formatter);
    var finalUTC = Date.UTC(
      Number(finalMap.year),
      Number(finalMap.month) - 1,
      Number(finalMap.day),
      Number(finalMap.hour),
      Number(finalMap.minute),
      Number(finalMap.second)
    );
    var finalDiff = desiredUTC - finalUTC;

    return new Date(adjusted.getTime() + finalDiff);
  }

  /**
   * Build Intl.DateTimeFormat for timezone
   * @param {string} zone - Timezone string
   * @returns {Intl.DateTimeFormat|null} Formatter or null if unsupported
   */
  function buildFormatter(zone) {
    try {
      return new Intl.DateTimeFormat('en-AU', {
        timeZone: zone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return null;
    }
  }

  /**
   * Convert formatToParts result to map
   * @param {Date} date - Date to format
   * @param {Intl.DateTimeFormat} formatter - Formatter instance
   * @returns {Object} Map of date parts
   */
  function partsToMap(date, formatter) {
    var pieces = formatter.formatToParts(date);
    var store = {};
    for (var i = 0; i < pieces.length; i += 1) {
      store[pieces[i].type] = pieces[i].value;
    }
    return store;
  }

  /**
   * Initialize all countdowns on page
   * Looks for elements with data-promo-countdown-section attribute
   */
  function initAll() {
    var sections = document.querySelectorAll('[data-promo-countdown-section]');
    for (var i = 0; i < sections.length; i++) {
      var sectionId = sections[i].id;
      if (sectionId) {
        init(sectionId);
      }
    }
  }

  function initSection(sectionEl) {
    if (!sectionEl) return;
    if (sectionEl.id) init(sectionEl.id);
  }

  function unloadSection(sectionEl) {
    if (!sectionEl) return;
    var countdowns = [];
    if (sectionEl.matches && sectionEl.matches('[data-countdown]')) countdowns.push(sectionEl);
    sectionEl.querySelectorAll('[data-countdown]').forEach(function (countdownEl) { countdowns.push(countdownEl); });
    countdowns.forEach(function (countdownEl) { stop(countdownEl, true); });
  }

  // Public API
  return {
    init: init,
    initAll: initAll,
    initSection: initSection,
    unloadSection: unloadSection
  };
})();

// Auto-initialize on DOM ready
if (!window.PromoCountdownListenersBound) {
  window.PromoCountdownListenersBound = true;
  document.addEventListener('shopify:section:load', function (event) {
    window.PromoCountdown.initSection(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    window.PromoCountdown.unloadSection(event.target);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { window.PromoCountdown.initAll(); });
  }
}
if (document.readyState !== 'loading') window.PromoCountdown.initAll();
